import React from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import { EosShiftData } from './eosReportingData';
import { formatWholeNumber } from '../utils';
import {
  pctToStatus,
  hcGapToStatus,
  percentOf,
  statusToAccentColor,
  EosStatus,
} from './eosReportingUtils';

/**
 * The 4 KPI (maestro) cards for one shift in Consolidated EOS Reporting (v6):
 * Volume Completed, TPH, Support Rate, HC Staffed. Each card is a Meridian Box
 * with a status-colored top accent bar (green/yellow/red). Presentational.
 */
export interface EosKpiCardsProps {
  shift: EosShiftData;
}

interface CardModel {
  key: string;
  label: string;
  hero: string;
  sub: string;
  plan: string;
  status: EosStatus;
}

const EosKpiCards: React.FC<EosKpiCardsProps> = ({ shift }) => {
  const cards: CardModel[] = [
    {
      key: 'volume',
      label: 'Volume Completed',
      hero: formatWholeNumber(shift.volume),
      sub: `${percentOf(shift.volume, shift.volumeTarget, 1)}% to ALPS`,
      plan: `ALPS Target: ${formatWholeNumber(shift.volumeTarget)}`,
      status: shift.volumeStatus ?? pctToStatus((shift.volume / shift.volumeTarget) * 100),
    },
    {
      key: 'tph',
      label: 'TPH',
      hero: `${shift.tph}`,
      sub: `${percentOf(shift.tph, shift.tphOP)}% to OP`,
      plan: `OP Target: ${shift.tphOP}`,
      status: shift.tphStatus ?? pctToStatus((shift.tph / shift.tphOP) * 100),
    },
    {
      key: 'supportRate',
      label: 'Support Rate',
      hero: shift.supportRate.toLocaleString(),
      sub: `${percentOf(shift.supportRate, shift.supportTarget)}% to target`,
      plan: `Target: ${shift.supportTarget.toLocaleString()}`,
      status: shift.supportStatus ?? pctToStatus((shift.supportRate / shift.supportTarget) * 100),
    },
    {
      key: 'hc',
      label: 'HC Staffed',
      hero: `${shift.hcStaffed} / ${shift.hcTarget}`,
      sub: `${percentOf(shift.hcStaffed, shift.hcTarget)}% to plan`,
      plan: `Target: ${shift.hcTarget}`,
      status: shift.hcStatus ?? hcGapToStatus(shift.hcStaffed, shift.hcTarget),
    },
  ];

  return (
    <Row spacing="400" widths="fill" alignmentVertical="stretch" data-testid={`eos-cards-${shift.key}`}>
      {cards.map(card => (
        <Box
          key={card.key}
          type="outline"
          spacingInset="none"
          data-testid={`eos-card-${shift.key}-${card.key}`}
          data-status={card.status}
        >
          <Column spacing="none">
            {/* Status-colored top accent bar — matches the In-Shift Maestro card. */}
            <Box backgroundColor={statusToAccentColor[card.status]} height={4} />
            <Box spacingInset="400">
              <Column spacing="200">
                <Text type="h100" color="secondary">
                  {card.label}
                </Text>
                <Heading level={2} type="h500">
                  {card.hero}
                </Heading>
                <Text type="b200" color="secondary">
                  {card.sub}
                </Text>
                <Text type="b200" color="secondary">
                  {card.plan}
                </Text>
              </Column>
            </Box>
          </Column>
        </Box>
      ))}
    </Row>
  );
};

export default EosKpiCards;
